/**
 * Punch list item field config for a project. Mirrors the API contract (see
 * ORG_ADMIN_ONLY_PROJECT_CONFIGS in project-model.js):
 *  - a project may carry an array of
 *    `{ name, display, display_order, label?, multiple? }` at
 *    source_entity_extra_attributes.punchlist_item_config, the same shape as
 *    its widget_config. `name` is a field of the item;
 *  - a non-empty array REPLACES the item form's built-in field list: the form
 *    shows the entries with `display: true`, in `display_order`, under the
 *    entry's `label` when it has one. Missing, null, empty or not an array
 *    leaves the built-in fields exactly as they are;
 *  - `multiple: true` on `category` / `type` lets the field take several
 *    values. Without it the field takes one, as it always has.
 *
 * There is no org-level counterpart. The pick-list values are not part of
 * this config: they are lookups, resolved by the API for the project.
 *
 * Photos / attachments, the map location and the nearby / linked issues are
 * not fields in this sense and are never governed by the config.
 *
 * Everything here is pure: the project payload is sent back to the API on a
 * project save and the API compares `punchlist_item_config` by value, so the
 * stored array must never be reordered or rewritten in place.
 */

/** The item's fields, in the form's built-in order. */
export const PUNCHLIST_ITEM_FIELD_NAMES = [
  'title',
  'description',
  'site_id',
  'bid_items',
  'category',
  'type',
  'severity',
  'need_by_date',
  'location_description',
  'proposed_solution',
] as const;

export type PunchlistItemFieldName = typeof PUNCHLIST_ITEM_FIELD_NAMES[number];

export interface PunchlistItemConfigEntry {
  name: string;
  display: boolean;
  display_order: number;
  label?: string;
  multiple?: boolean;
}

/** A field as a screen renders it. */
export interface PunchlistItemField {
  name: PunchlistItemFieldName;
  /** The project's label for the field; null where the screen's own label stands. */
  label: string | null;
  /** The field takes several values. Category and type only. */
  multiple: boolean;
}

export type PunchlistItemFieldMap = Partial<Record<PunchlistItemFieldName, PunchlistItemField>>;

/** The slice of a project payload the field config is read from. */
export interface PunchlistItemConfigProject {
  source_entity_extra_attributes?: Record<string, unknown> | null;
}

/** The API requires a title, so no config can take it off the form. */
const REQUIRED_FIELD: PunchlistItemFieldName = 'title';

/** Fields that can hold several values (the item's `categories` / `types`). */
const MULTI_VALUE_FIELDS: PunchlistItemFieldName[] = ['category', 'type'];

/**
 * Where each field travels in the create / update payload. Category and type
 * go as arrays; the API derives the single `category` / `type` strings.
 */
const PAYLOAD_KEYS: Record<PunchlistItemFieldName, string> = {
  title: 'title',
  description: 'description',
  site_id: 'site_id',
  bid_items: 'bid_item_ids',
  category: 'categories',
  type: 'types',
  severity: 'severity',
  need_by_date: 'need_by_date',
  location_description: 'location_description',
  proposed_solution: 'proposed_solution',
};

const isFieldName = (name: string): name is PunchlistItemFieldName =>
  (PUNCHLIST_ITEM_FIELD_NAMES as readonly string[]).includes(name);

const builtInFields = (): PunchlistItemField[] =>
  PUNCHLIST_ITEM_FIELD_NAMES.map(name => ({ name, label: null, multiple: false }));

/** Entries without a usable order go last, in the order they were written. */
const orderOf = (entry: PunchlistItemConfigEntry): number =>
  Number.isFinite(entry.display_order) ? entry.display_order : Number.MAX_SAFE_INTEGER;

/**
 * The fields the punch list item screens show for `project`, in order.
 *
 * With no usable config these are the built-in fields. With one, they are its
 * displayed entries; names that are not fields of the item are skipped, a
 * name listed twice counts once (its first entry in display order), and the
 * title is always there: where the config puts it, or first when the config
 * leaves it out.
 */
export function resolvePunchlistItemFields(
  project: PunchlistItemConfigProject | null | undefined
): PunchlistItemField[] {
  const config = project?.source_entity_extra_attributes?.punchlist_item_config;
  // Entries without a name cannot be rendered; a config with none left is
  // not a config.
  const entries: PunchlistItemConfigEntry[] = Array.isArray(config)
    ? config.filter(entry => typeof entry?.name === 'string' && entry.name !== '')
    : [];
  if (entries.length === 0) {
    return builtInFields();
  }

  const ordered = entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => orderOf(a.entry) - orderOf(b.entry) || a.index - b.index);

  const seen = new Set<string>();
  const fields: PunchlistItemField[] = [];
  ordered.forEach(({ entry }) => {
    const { name } = entry;
    if (!isFieldName(name) || seen.has(name)) {
      return;
    }
    seen.add(name);
    if (!entry.display && name !== REQUIRED_FIELD) {
      return;
    }
    const label = typeof entry.label === 'string' ? entry.label.trim() : '';
    fields.push({
      name,
      label: label || null,
      multiple: MULTI_VALUE_FIELDS.includes(name) && entry.multiple === true,
    });
  });

  if (!seen.has(REQUIRED_FIELD)) {
    fields.unshift({ name: REQUIRED_FIELD, label: null, multiple: false });
  }
  return fields;
}

/** `fields` keyed by name: a field is shown when it has an entry. */
export function punchlistItemFieldMap(fields: PunchlistItemField[] | null | undefined): PunchlistItemFieldMap {
  return (fields || []).reduce((acc: PunchlistItemFieldMap, field) => {
    acc[field.name] = field;
    return acc;
  }, {});
}

/**
 * `payload` without the fields this project hides.
 *
 * The API leaves a field that is absent from an update untouched and clears
 * one that is present but empty (null, '' or []). A hidden field has nothing
 * on screen to edit, so it must not be sent at all: sending the form's empty
 * control would wipe a value stored before the field was hidden, or by the
 * mobile app. On a create an absent field is stored empty, which is what a
 * hidden field should be.
 */
export function withoutHiddenPunchlistItemFields<T extends Record<string, unknown>>(
  payload: T,
  fields: PunchlistItemField[]
): Partial<T> {
  const shown = punchlistItemFieldMap(fields);
  const result: Record<string, unknown> = { ...payload };
  PUNCHLIST_ITEM_FIELD_NAMES
    .filter(name => !shown[name] && name !== REQUIRED_FIELD)
    .forEach(name => delete result[PAYLOAD_KEYS[name]]);
  return result as Partial<T>;
}
